export const modal_ui_styles = {
    overlay: 'z-49 bg-teal/90 backdrop-blur-xs pointer-events-none',
    content: 'z-50 max-w-[56.25rem] text-teal bg-white border-0',
    header: 'border-0 min-h-0',
    body: 'flex flex-col content-center items-center border-0',
    footer: 'border-0 flex flex-col content-center items-center xl:pb-[3.4375rem]',
    title: 'invisible h-0',
    // Match the title: these modals show their own body copy, so the reka-
    // required DialogDescription exists only for accessibility, kept out of view.
    description: 'invisible h-0'
}

export const close_button_ui_styles = {
    color: 'transparent',
    variant: 'close',
    class: 'rounded-full border-teal border-1 hover:bg-teal hover:text-white',
}

export const drawer_close_button_ui_styles = {
    default: 'rounded-full bg-teal border-teal border-1 hover:bg-teal hover:text-white',
}

export const search_ui_styles = {
    content: 'bg-teal text-white',
    header: 'border-0 flex flex-col content-center items-end',
    body: 'flex flex-col justify-center border-0 h-full',
    footer: '',
    title: 'invisible h-0',
    description: 'invisible h-0',
}

export const registration_modal_ui_styles = {
    content: 'bg-teal text-white',
    header: 'border-0 flex flex-col content-center items-end',
    body: 'flex flex-col justify-center border-0 h-full',
    footer: '',
    title: 'invisible h-0',
    description: 'invisible h-0',
}

export const modal_2026_ui_styles = {
    overlay: 'bg-[var(--pink)]',
    content: 'bg-[var(--pink)] text-[var(--magenta)]',
    header: 'border-0 flex flex-col content-center items-end p-[30px]',
    body: 'flex flex-col items-center border-0 h-full',
    footer: '',
    title: 'sr-only',
    description: 'sr-only',
}

export const close_button_2026_styles = {
    icon: 'i-lucide-x',
    color: 'neutral',
    variant: 'ghost',
    // modal-shell-2026__close is a PLAIN class, not a utility: Tailwind never scans
    // this file, so a `bg-transparent` written here is silently never generated
    // (verified — the rule does not exist in the stylesheet). The transparent
    // background is set from shell.vue's own CSS instead. Nuxt UI's ghost button
    // otherwise resolves to a near-white resting fill, which read as a white disc
    // around the ✕ once the modal went full-bleed on mobile.
    // NOTE: the hover: utilities below are NOT generated — Tailwind does not scan
    // this file, which is the same reason the bracketed colours here are set from
    // shell.vue's CSS instead (see the note above). Both the hover fill AND the
    // glyph inversion live in .modal-shell-2026__close:hover. Left in place only
    // because the resting colours read from here; do not rely on them.
    class: 'modal-shell-2026__close text-[var(--magenta)] border border-[var(--magenta)] rounded-full',
}

// The 2026 modal button (Figma): pill, 20/10 padding, HubSpot Serif 17/1.4 with
// 0.34px tracking. Several modals had drifted to 28px padding at 15px, which read
// as small buttons with too much side padding — hoisted here so they can't drift
// apart again.
// Colours use the (--var) shorthand, not [var(--var)]: the bracketed form isn't
// reliably extracted from these JS class strings by Tailwind here, so hover
// variants silently no-op'd (the filled hover stayed magenta instead of purple).
const modal_button_2026_base = 'rounded-[50px] px-[20px] py-[10px] font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px] cursor-pointer transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta)'

export const modal_button_outline_2026 = `${modal_button_2026_base} border border-(--magenta) text-(--magenta) hover:bg-(--magenta) hover:text-white`

export const modal_button_filled_2026 = `${modal_button_2026_base} bg-(--magenta) border border-(--magenta) text-white hover:bg-(--purple) hover:border-(--purple)`

// The site's form-error treatment, taken from the login form (auth/form.vue),
// which is the standard Nick asked these fields to match: the field's stroke and
// the message below it both go #980000, and the message is medium-weight and
// left-aligned with the field.
//
// The schedule-preference fields had invented their own — a softer pink
// (#ad0065), centred, regular weight — which read as decoration rather than an
// error and was missed entirely in QA (Asana 687576 / UNB-595). Note the
// --error token (#EB0004) is NOT this: it's legacy, used only by the HubSpot
// form embed, and matching it would look like a third error style.
export const form_field_error_border_2026 = 'border-[#980000]'

const form_error_text_2026_base = 'text-[13px] font-hubspot-sans font-medium text-[#980000] text-left w-full'

// Form-level message (one error for the whole form), which owns its own spacing.
export const form_error_text_2026 = `mt-[16px] ${form_error_text_2026_base}`

// Per-FIELD variant, for forms that validate each field separately (Create My
// Event, UNB-626 sibling tickets). The message sits inside the field wrapper's
// own 10px gap, so it carries no top margin. Leading/tracking are the exact
// values from that design (Figma 4683-93014); colour, weight and size are
// identical to the form-level treatment above so the two never look like
// different error styles.
export const form_field_error_text_2026 = `${form_error_text_2026_base} leading-[1.5] tracking-[0.26px]`

// The "Build My Schedule" CTA: darker-pink, going magenta on hover. Deliberately
// NOT the shared filled button above (magenta → purple) — it matches the
// .ms-cta-build treatment the agenda already uses on every other Build entry
// point, so the same action looks the same wherever it appears (Asana 687571).
//
// Composed from the base rather than layered onto modal_button_filled_2026:
// appending a second bg-* utility doesn't reliably win, because Tailwind resolves
// same-specificity classes by stylesheet order, not by class-attribute order.
export const modal_button_build_2026 = `${modal_button_2026_base} bg-(--darker-pink) border border-(--darker-pink) text-white hover:bg-(--magenta) hover:border-(--magenta)`

// The 2026 modal "Close" text link (Figma): Sans Light 14px, darker-pink, dotted
// underline. On hover the underline drops and the text turns purple (#820CA7).
// Shared so every modal's Close behaves identically.
export const modal_close_link_2026 = 'font-hubspot-sans font-light text-[14px] leading-[1.5] tracking-[0.28px] text-[#7d4566] underline decoration-dotted underline-offset-[3px] cursor-pointer bg-transparent border-none p-0 transition-colors duration-200 hover:text-(--purple) hover:no-underline'